import isEqual from 'lodash/isEqual';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { RootState } from '../../app-state/store';
import { useLayoutStateSWR } from '../../helpers/api';
import pathMap from '../../page-config/pathMap';
function PageComponentRenderer({
    component,
    pageConfig,
    t,
    pageLayoutState,
    setPageLayoutState,
    children,
    disClosure,
    ...props
}: {
    component: PageComponent;
    t: any;
    pageLayoutState: Record<string, any>;
    setPageLayoutState: React.Dispatch<
        React.SetStateAction<Record<string, any>>
    >;
    key: string;
    pageConfig?: PageConfig;
    children?: React.ReactNode;
    disClosure?: any;
    doHeightAjust?: any;
}) {
    const router = useNavigate();
    const { state } = useLocation();
    const appState: any = useSelector((state: RootState) => state);
    const [layoutState, setLayoutState] = useState<Record<string, any>>({});
    const [storeInfo, setStoreInfo] = useState<Record<string, any>>({});

    const PageComponent: any = pathMap?.[component?.name];
    const [previousLocalState, setPreviousLocalState] = useState<
        Record<string, any>
    >({});
    const [localLayoutState, setLocalLayoutState] = useState<
        Record<string, any>
    >({});
    const [endPointParams, setEndPointParams] = useState<string[] | null>(null);

    const dataLoadObject = useLayoutStateSWR(
        component.apiDetails,
        localLayoutState
    );

    const updateLocalState = () => {
        let _previousLocalState: Record<string, any> = {};

        endPointParams?.forEach((item: string) => {
            if (item.startsWith('*')) {
                item = item.substr(2, item.length);
            }
            const keys = item.split(',');
            keys.forEach((key: string) => {
                _previousLocalState[key] = layoutState[key];
            });
        });
        if (!isEqual(previousLocalState, _previousLocalState)) {
            setPreviousLocalState(() => {
                return { ..._previousLocalState };
            });
            setLocalLayoutState({ ...layoutState });
        }
    };

    useEffect(() => {
        if (!endPointParams) {
            return;
        }
        updateLocalState();
    }, [layoutState]);

    useEffect(() => {
        setLayoutState((layoutState: any) => {
            return {
                ...layoutState,
                ...pageLayoutState,
            };
        });
    }, [pageLayoutState]);

    useEffect(() => {
        if (component?.fromStore && appState) {
            const params: Record<string, any> = {};
            const { fromStore } = component;
            fromStore?.forEach((state: Record<any, any>) => {
                params[state?.value] = appState?.[state?.key]?.[state?.value];
            });
            setStoreInfo(params);
        }
    }, [component?.fromStore, appState]);

    useEffect(() => {
        component?.apiDetails?.endpointParams &&
            setEndPointParams(
                Object.values(component?.apiDetails?.endpointParams)
            );
    }, [component?.apiDetails?.endpointParams]);

    useEffect(() => {
        endPointParams && updateLocalState();
    }, [endPointParams]);

    return (
        <PageComponent
            pageProps={{
                ...pageConfig?.pageProps,
            }}
            meta={{
                processedEndpoint: dataLoadObject?.url,
                endpointVersion: dataLoadObject?.endpointVersion,
                endpointDomain: dataLoadObject?.endpointDomain,
            }}
            width="100%"
            height="full"
            layoutState={layoutState}
            setLayoutState={setLayoutState}
            pageLayoutState={pageLayoutState}
            setPageLayoutState={setPageLayoutState}
            dataLoad={dataLoadObject}
            disClosure={disClosure}
            globalProps={{
                redirectTo: (url: string, options?: Record<string, any>) => {
                    options = options || {};
                    if (options?.search) {
                        url &&
                            router({
                                pathname: url,
                                search: `?${createSearchParams(
                                    options?.search
                                )}`,
                            });
                    } else {
                        url && router(url, { ...options });
                    }
                },
                t: t,
                qp: state,
            }}
            {...component.props}
            {...storeInfo}
            {...props}>
            {children}
        </PageComponent>
    );
}

export default PageComponentRenderer;
